import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Socket } from 'socket.io-client';
import {
  LIVE_CLIENT_EVENTS,
  LIVE_FALLBACK_AFTER_SECONDS,
  LIVE_PATH,
  SUPPORT_LIVE_EVENTS,
  supportRooms,
  type LiveAck,
  type SupportMessage,
  type SupportTicketEvent,
  type TicketView,
} from '@capsico/contracts';
import { api } from '../api/client';
import { mergeSupportMessage } from './supportMerge';

export const supportTicketKey = (id: string) => ['support-ticket', id] as const;
export const supportTicketsKey = ['support-tickets'] as const;

/** The API origin (the live socket sits on the API server under LIVE_PATH). */
function liveOrigin(): string {
  const base = api.defaults.baseURL ?? window.location.origin;
  try {
    return new URL(base, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

/**
 * Live chat for one ticket (W16 §8, the W14 live hub). Joins `ticket:<id>`; new messages are merged
 * into the cached TicketView at once and a ticket change (status, agent, version) refetches it.
 * While the socket has been down for more than LIVE_FALLBACK_AFTER_SECONDS, `polling` turns on and
 * the ticket screen polls every SUPPORT_POLL_SECONDS instead. Reconnects are silent.
 */
export function useSupportLive(ticketId: string, enabled: boolean): { polling: boolean } {
  const client = useQueryClient();
  const [connection, setConnection] = useState<{ connected: boolean; downSince: number | null }>({
    connected: false,
    downSince: null,
  });
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled || !ticketId) return;
    const room = supportRooms.ticket(ticketId);
    const key = supportTicketKey(ticketId);
    let socket: Socket | null = null;
    let cancelled = false;

    const markDown = () => {
      const at = Date.now();
      setNow(at);
      setConnection((current) =>
        current.connected || current.downSince === null
          ? { connected: false, downSince: at }
          : current,
      );
    };
    // The "down" clock starts now: if the first connect never succeeds the poll still takes over.
    markDown();

    // socket.io-client loads only on a chat screen, so other screens never pay for it.
    void import('socket.io-client')
      .then(({ io }) => {
        if (cancelled) return;
        const live = io(liveOrigin(), {
          path: LIVE_PATH,
          withCredentials: true,
          transports: ['websocket', 'polling'],
          reconnectionDelay: 1000,
          reconnectionDelayMax: 10_000,
        });
        socket = live;
        live.on('connect', () => {
          live.emit(LIVE_CLIENT_EVENTS.subscribe, { room }, (ack: LiveAck | undefined) => {
            if (ack?.ok) {
              setConnection({ connected: true, downSince: null });
              // Catch up on anything missed while away.
              void client.invalidateQueries({ queryKey: key, exact: true });
            } else {
              markDown();
            }
          });
        });
        live.on('disconnect', markDown);
        live.on('connect_error', markDown);
        live.on(SUPPORT_LIVE_EVENTS.message, (message: SupportMessage) => {
          if (!message?.id || message.ticketId !== ticketId) return;
          client.setQueryData<TicketView | undefined>(key, (current) =>
            mergeSupportMessage(current, message),
          );
          void client.invalidateQueries({ queryKey: supportTicketsKey });
        });
        live.on(SUPPORT_LIVE_EVENTS.ticket, (event: SupportTicketEvent) => {
          if (!event || event.ticketId !== ticketId) return;
          const cached = client.getQueryData<TicketView>(key);
          if (!cached || event.version > cached.version) {
            void client.invalidateQueries({ queryKey: key, exact: true });
            void client.invalidateQueries({ queryKey: supportTicketsKey });
          }
        });
      })
      .catch(markDown);

    return () => {
      cancelled = true;
      if (socket) {
        socket.emit(LIVE_CLIENT_EVENTS.unsubscribe, { room });
        socket.removeAllListeners();
        socket.disconnect();
      }
      setConnection({ connected: false, downSince: null });
    };
  }, [ticketId, enabled, client]);

  const down =
    !connection.connected &&
    connection.downSince !== null &&
    now - connection.downSince >= LIVE_FALLBACK_AFTER_SECONDS * 1000;

  // While the socket is down (but not yet long enough), tick once a second to notice the threshold.
  useEffect(() => {
    if (!enabled || connection.connected || connection.downSince === null || down) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [enabled, connection.connected, connection.downSince, down]);

  return { polling: enabled && down };
}
